/**
 * Timeline component: audio waveform, playback controls, beat markers.
 * Uses Web Audio API AnalyserNode for waveform visualization.
 * LyricAI-inspired design with purple/pink accents.
 *
 * Wired to store: playbackSpeed, musicVolume, loopEnabled, waveformVisible.
 * Keyboard shortcuts moved to useKeyboardShortcuts hook.
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { getAudioUrl } from '../../api/client';
import { checkNoteCrossings, resetTickState, playTick } from '../../audio/tickSound';
import { playScrollTick } from '../../editors/flow/flowSounds';
import { setSrAnalyser } from '../../audio/srAnalyser';
import { registerAudioElement, seekAudio } from '../../audio/playbackController';
import { color, font } from '../ui/theme';
import { tintOklch } from '../ui/themeColors';

// Layout — matches the Flow / Console waveform pill exactly.
const PAD_Y        = 8;     // vertical inset of the content band
const TOTAL_HEIGHT = 48;    // matches the playback pill height
const CONTENT_H    = TOTAL_HEIGHT - PAD_Y * 2;
const TOP          = PAD_Y;

/** Format seconds as m:ss */
function formatTime(s: number) {
  const min = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

export default function Timeline() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const selCanvasRef = useRef<HTMLCanvasElement>(null);   // marching-ants selection overlay
  const dashOffsetRef = useRef(0);
  const restoredRef = useRef(false);                      // restore scrub position once on load
  const wrapperRef = useRef<HTMLDivElement>(null);
  const playheadRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const animFrameRef = useRef<number>(0);
  const prevBeatRef = useRef<number>(0);
  // Static full-song waveform peaks (max abs amplitude per bucket), decoded once
  // per track — drawn behind the markers like the Flow editor, instead of the
  // live analyser oscilloscope (which is flat whenever playback is paused).
  const peaksRef = useRef<{ data: Float32Array; dur: number } | null>(null);
  const [audioReady, setAudioReady] = React.useState(false);
  // Bumped after the waveform peaks decode so the static canvas repaints.
  const [peaksVersion, setPeaksVersion] = React.useState(0);

  const beatmap = useEditorStore(s => s.beatmap);
  const activeDifficulty = useEditorStore(s => s.activeDifficulty);
  // Subscribe only to playing & duration (change infrequently).
  // currentTime & currentBeat update 60x/sec — read imperatively in the
  // draw loop and push to DOM refs so the component never re-renders for them.
  const pbPlaying = useEditorStore(s => s.playback.playing);
  const pbDuration = useEditorStore(s => s.playback.duration);
  const timeDisplayRef = useRef<HTMLSpanElement>(null);
  const beatDisplayRef = useRef<HTMLSpanElement>(null);
  const setPlaying = useEditorStore(s => s.setPlaying);
  const setCurrentTime = useEditorStore(s => s.setCurrentTime);
  const setDuration = useEditorStore(s => s.setDuration);

  // New store wiring
  const playbackSpeed = useEditorStore(s => s.playbackSpeed);
  const musicVolume = useEditorStore(s => s.musicVolume);
  const loopEnabled = useEditorStore(s => s.loopEnabled);
  const waveformVisible = useEditorStore(s => s.waveformVisible);
  // waveformVisible is read from store inside the draw loop via getState()

  // Undo / Redo / Lock (moved from header)
  const undo = useEditorStore(s => s.undo);
  const redo = useEditorStore(s => s.redo);
  const undoStack = useEditorStore(s => s.undoStack);
  const redoStack = useEditorStore(s => s.redoStack);
  const mapLocked = useEditorStore(s => s.mapLocked);
  const setMapLocked = useEditorStore(s => s.setMapLocked);

  const difficulty = beatmap?.difficulties[activeDifficulty];

  // ── Register the audio element so other components (e.g. the Pattern
  // Difficulty heatmap) can seek it via playbackController.seekAudio.
  useEffect(() => {
    registerAudioElement(audioRef.current);
    return () => registerAudioElement(null);
  }, []);

  // ── Setup audio ───────────────────────────────────────────────

  useEffect(() => {
    if (!beatmap?.id || !beatmap.has_audio) return;

    const audio = audioRef.current;
    if (!audio) return;

    audio.src = getAudioUrl(beatmap.id);
    audio.crossOrigin = 'anonymous';
    setAudioReady(false);

    const onLoaded = () => {
      setDuration(audio.duration);
      setAudioReady(true);

      // Restore the last scrub position (refresh) once, after the audio's
      // duration is known. Seek the audio + sync the store playhead.
      if (!restoredRef.current) {
        restoredRef.current = true;
        const t = useEditorStore.getState().lastEditTime;
        if (t > 0.05 && t < audio.duration - 0.05) {
          audio.currentTime = t;
          setCurrentTime(t);
        }
      }

      // Setup Web Audio analyser
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContext();
      }
      const ctx = audioCtxRef.current;

      if (!sourceRef.current) {
        sourceRef.current = ctx.createMediaElementSource(audio);
      }

      // Only reconnect analyser if not already set up
      if (!analyserRef.current) {
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        sourceRef.current.connect(analyser);
        analyser.connect(ctx.destination);
        analyserRef.current = analyser;
        setSrAnalyser(analyser);   // expose for the playback-bar audio bars
      }
    };

    audio.addEventListener('loadedmetadata', onLoaded);
    // canplaythrough as fallback — some browsers skip loadedmetadata on cached audio
    audio.addEventListener('canplaythrough', onLoaded);

    // If audio is already cached / metadata already loaded, fire manually
    if (audio.readyState >= 1) {
      onLoaded();
    }

    // When audio ends naturally (not looping), sync the store back to stopped
    const onEnded = () => {
      if (!audio.loop) {
        setPlaying(false);
        setCurrentTime(audio.duration);
      }
    };
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('canplaythrough', onLoaded);
      audio.removeEventListener('ended', onEnded);
    };
  }, [beatmap?.id, beatmap?.has_audio, setDuration, setPlaying, setCurrentTime]);

  // ── Tear down the analyser AudioContext on unmount ────────────
  // Whole SR editor (Timeline included) unmounts when the user switches to
  // Flow/Console/dashboard (App.tsx conditionally renders one editor tree at a
  // time). Without this, every switch away leaves the previous AudioContext
  // running in the background forever (it's never GC'd — closing is the only
  // way to stop it), and switching back creates a brand new one. A session
  // that hops between editors a few times ends up with several live contexts
  // each burning a real-time audio thread, which drags everything down.
  useEffect(() => {
    return () => {
      setSrAnalyser(null);
      try { sourceRef.current?.disconnect(); } catch { /* already gone */ }
      try { analyserRef.current?.disconnect(); } catch { /* already gone */ }
      sourceRef.current = null;
      analyserRef.current = null;
      const ctx = audioCtxRef.current;
      audioCtxRef.current = null;
      if (ctx && ctx.state !== 'closed') ctx.close().catch(() => {});
    };
  }, []);

  // ── Decode the full-song waveform peaks (static, like Flow) ──
  useEffect(() => {
    if (!beatmap?.id || !beatmap.has_audio) { peaksRef.current = null; return; }
    let alive = true;
    (async () => {
      try {
        const buf = await (await fetch(getAudioUrl(beatmap.id))).arrayBuffer();
        const Ctx: typeof AudioContext =
          window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new Ctx();
        const audio = await ctx.decodeAudioData(buf);
        ctx.close().catch(() => {});
        const ch = audio.getChannelData(0);
        const BUCKETS = 2048;
        const per = Math.max(1, Math.floor(ch.length / BUCKETS));
        const out = new Float32Array(BUCKETS);
        for (let b = 0; b < BUCKETS; b++) {
          let peak = 0;
          const base = b * per;
          for (let i = 0; i < per; i++) {
            const v = Math.abs(ch[base + i] || 0);
            if (v > peak) peak = v;
          }
          out[b] = peak;
        }
        if (alive) { peaksRef.current = { data: out, dur: audio.duration }; setPeaksVersion(v => v + 1); }
      } catch { /* ignore decode errors — waveform just won't draw */ }
    })();
    return () => { alive = false; };
  }, [beatmap?.id, beatmap?.has_audio]);

  // ── Wire playbackSpeed → audio.playbackRate ─────────────────

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = playbackSpeed;
  }, [playbackSpeed]);

  // ── Wire musicVolume → audio.volume ─────────────────────────

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = musicVolume;
  }, [musicVolume]);

  // ── Wire loopEnabled → audio.loop ───────────────────────────

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.loop = loopEnabled;
  }, [loopEnabled]);

  // ── Sync playback state ───────────────────────────────────────

  const playRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Clear any pending retry
    if (playRetryRef.current) {
      clearTimeout(playRetryRef.current);
      playRetryRef.current = null;
    }

    if (pbPlaying) {
      // If audio isn't ready yet, wait — the effect will re-run when audioReady changes
      if (!audioReady) {
        // Revert playing state so UI doesn't show playing while audio isn't loaded
        setPlaying(false);
        return;
      }

      // Resume AudioContext if suspended (browsers require user gesture)
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume();
      }
      // Sync audio element to the store's current position before playing
      // (the user may have scrolled in the 3D view which updates the store but not the audio)
      const storeTime = useEditorStore.getState().playback.currentTime;
      if (Math.abs(audio.currentTime - storeTime) > 0.05) {
        audio.currentTime = storeTime;
      }
      resetTickState();
      const _initState = useEditorStore.getState();
      const _initBpm = _initState.beatmap?.bpm || 120;
      prevBeatRef.current = ((audio.currentTime - (_initState.beatmap?.offset || 0) / 1000) / 60) * _initBpm;
      audio.play().catch((err) => {
        console.warn('Audio play failed:', err);
        // If play fails, revert playing state so UI stays in sync
        setPlaying(false);
      });
    } else {
      audio.pause();
      resetTickState();
    }
  }, [pbPlaying, audioReady, setPlaying]);

  // ── Watchdog: detect store=playing but audio.paused desync ───

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Check every 300ms: if the store says playing but the audio element is paused,
    // attempt to re-trigger play. This handles browser autoplay blocks, race
    // conditions where .play() resolves but audio doesn't actually start, and
    // transient audio element glitches.
    const watchdog = setInterval(() => {
      const state = useEditorStore.getState();
      if (state.playback.playing && audio.paused && audioReady) {
        // Resume AudioContext first (may have re-suspended)
        if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
          audioCtxRef.current.resume();
        }
        // Sync position and retry play
        const storeTime = state.playback.currentTime;
        if (Math.abs(audio.currentTime - storeTime) > 0.05) {
          audio.currentTime = storeTime;
        }
        audio.play().catch(() => {
          // After repeated failures, give up and sync the store
          setPlaying(false);
        });
      }
    }, 300);

    return () => clearInterval(watchdog);
  }, [audioReady, setPlaying]);

  // ── Static canvas paint (full song, like Flow/Console) ───────────────────
  // Drawn once per data change (notes / peaks / resize), NOT every frame — the
  // playhead is a DOM div moved imperatively in the RAF below.
  const paint = useCallback(() => {
    const canvas  = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const dpr  = window.devicePixelRatio || 1;
    const cssW = wrapper.clientWidth;
    if (cssW <= 0) return;
    if (canvas.width  !== Math.floor(cssW * dpr) ||
        canvas.height !== Math.floor(TOTAL_HEIGHT * dpr)) {
      canvas.width  = Math.floor(cssW * dpr);
      canvas.height = Math.floor(TOTAL_HEIGHT * dpr);
      canvas.style.width  = cssW + 'px';
      canvas.style.height = TOTAL_HEIGHT + 'px';
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, TOTAL_HEIGHT);

    // One uniform, opaque dark fill edge-to-edge (single container, no tint).
    ctx.fillStyle = tintOklch(0.075, 0.016, 1);
    ctx.fillRect(0, 0, cssW, TOTAL_HEIGHT);

    const songDur = Math.max(useEditorStore.getState().playback.duration, 0.01);
    // Inset content by the pill's corner radius so it starts/ends at the
    // straight edge — never under the round caps.
    const PAD_X = TOTAL_HEIGHT / 2;
    const drawW = Math.max(1, cssW - PAD_X * 2);
    const timeToX = (t: number) => PAD_X + (t / songDur) * drawW;

    // Faint grid lines (Flow-style, no number labels).
    const tickEvery = songDur > 180 ? 60 : songDur > 90 ? 30 : 10;
    ctx.strokeStyle = tintOklch(0.55, 0.09, 0.22);
    ctx.setLineDash([]);
    ctx.lineWidth = 1;
    for (let s = tickEvery; s <= songDur; s += tickEvery) {
      const x = timeToX(s);
      ctx.beginPath();
      ctx.moveTo(x, TOP);
      ctx.lineTo(x, TOP + CONTENT_H);
      ctx.stroke();
    }

    // Waveform peaks (inset, centred in the content band). Read from the reactive
    // subscription, not getState(), so toggling it actually repaints — as an
    // imperative read it was invisible to paint's deps and the strip only changed
    // after some unrelated repaint (a note edit or a resize).
    if (waveformVisible && peaksRef.current) {
      const { data, dur } = peaksRef.current;
      const midY  = TOP + CONTENT_H / 2;
      const halfH = CONTENT_H / 2 - 1;
      ctx.fillStyle = tintOklch(0.58, 0.11, 0.30);
      for (let b = 0; b < data.length; b++) {
        const t = (b / data.length) * dur;
        const x = timeToX(t);
        if (x < PAD_X || x > cssW - PAD_X) continue;
        const h = data[b] * halfH;
        ctx.fillRect(x, midY - h, 1, h * 2);
      }
    }

    // Notes — colour-coded DOTS in 4 rows by type (Right / Left / One-hand /
    // Both-hands), with rails drawn as a faded line trailing each dot.
    // Obstacles are intentionally not drawn.
    if (difficulty) {
      const gp = useEditorStore.getState().gameplaySettings;
      const TIME_SCALE = 20; // rail_nodes z = time_seconds * TIME_SCALE
      const catOf = (n: { note_type: string; hand: string }): { row: number; color: string } => {
        const t = (n.note_type || '').toLowerCase();
        if (t.includes('both')) return { row: 3, color: gp.noteColorBothHands };
        if (t.includes('one') || t.includes('special')) return { row: 2, color: gp.noteColorOneHand };
        if (n.hand === 'left') return { row: 1, color: gp.noteColorLeft };
        return { row: 0, color: gp.noteColorRight };
      };
      // 4 rows packed into a compact band centred on the waveform.
      const bandH = CONTENT_H * 0.6;
      const bandTop = TOP + (CONTENT_H - bandH) / 2;
      const rowH = bandH / 4;
      const radius = 2.2;
      for (const note of difficulty.notes) {
        const x = timeToX(note.time_seconds);
        if (x < PAD_X - 3 || x > cssW - PAD_X + 3) continue;
        const { row, color } = catOf(note);
        const y = bandTop + (row + 0.5) * rowH;
        // Rail / line — drawn first so the head dot sits on top.
        if (note.has_rail && note.rail_nodes && note.rail_nodes.length > 0) {
          const endSec = note.rail_nodes[note.rail_nodes.length - 1][2] / TIME_SCALE;
          const xEnd = timeToX(endSec);
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.5;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(Math.max(x + 1, xEnd), y);
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
        // Head dot.
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }, [difficulty, peaksVersion, waveformVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Repaint on data change + on resize.
  useEffect(() => { paint(); }, [paint]);
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver(() => paint());
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [paint]);

  // Wheel-to-scrub over the waveform — mirrors the 3D scene's scroll handler
  // (steps by `scrollStep` beats; Ctrl+wheel and playback are left alone).
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const onWheel = (e: WheelEvent) => {
      const st = useEditorStore.getState();
      // Ctrl+wheel cycles step size (handled elsewhere) — leave it alone.
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      // Scrub works while playing too (like Flow): seek the audio to the new
      // position and keep playing from there.
      const isPlaying = st.playback.playing;

      // Shift+wheel — jump to the previous/next actual note (ignores the snap).
      if (e.shiftKey) {
        const diff = st.beatmap?.difficulties[st.activeDifficulty];
        if (diff && diff.notes.length) {
          const offsetSec = (st.beatmap?.offset || 0) / 1000; // note times are chart-relative
          const cur = st.playback.currentTime;
          const eps = 0.001;
          let target: number | null = null;
          if (e.deltaY < 0) {
            for (const n of diff.notes) { const t = n.time_ms / 1000 + offsetSec; if (t > cur + eps) { target = t; break; } }
          } else {
            for (let i = diff.notes.length - 1; i >= 0; i--) { const t = diff.notes[i].time_ms / 1000 + offsetSec; if (t < cur - eps) { target = t; break; } }
          }
          if (target !== null) { st.setCurrentTime(target); if (isPlaying) seekAudio(target); playScrollTick(); }
        }
        return;
      }

      const step = st.scrollStep || 1;
      const delta = e.deltaY > 0 ? -step : step;
      const dur = st.playback.duration;
      const maxBeat = dur > 0
        ? ((dur - (st.beatmap?.offset || 0) / 1000) / 60) * (st.beatmap?.bpm || 120)
        : Infinity;
      const newBeat = Math.min(maxBeat, Math.max(0, st.playback.currentBeat + delta));
      if (newBeat !== st.playback.currentBeat) {
        st.setCurrentBeat(newBeat);
        if (isPlaying) {
          const bpm = st.beatmap?.bpm || 120;
          const offsetSec = (st.beatmap?.offset || 0) / 1000; // offset is stored in ms
          seekAudio((newBeat / bpm) * 60 + offsetSec);
        }
        playScrollTick();
      }
    };
    wrapper.addEventListener('wheel', onWheel, { passive: false });
    return () => wrapper.removeEventListener('wheel', onWheel);
  }, []);

  // ── RAF loop: drive playback time + tick crossings + move the DOM playhead ──
  useEffect(() => {
    const loop = () => {
      const audio    = audioRef.current;
      const wrapper  = wrapperRef.current;
      const playhead = playheadRef.current;
      if (audio) {
        if (!audio.paused) {
          // Loop region (right-drag): jump back to the start once we pass the end.
          // Loop beats are CHART beats; their audio time = beat*60/bpm + offset.
          const ls = useEditorStore.getState();
          const offsetSec = (ls.beatmap?.offset || 0) / 1000;
          if (ls.loopRegion) {
            const bpm = ls.beatmap?.bpm || 120;
            if (audio.currentTime >= ls.loopRegion.endBeat * 60 / bpm + offsetSec) {
              audio.currentTime = ls.loopRegion.startBeat * 60 / bpm + offsetSec;
            }
          }
          setCurrentTime(audio.currentTime);
          const state = useEditorStore.getState();
          const _bpm = state.beatmap?.bpm || 120;
          // Offset-adjusted chart beat so note-crossing ticks fire with the music.
          const rawCurrentBeat = ((audio.currentTime - offsetSec) / 60) * _bpm;
          const diff = state.beatmap?.difficulties[state.activeDifficulty];
          if (diff?.notes) checkNoteCrossings(diff.notes, prevBeatRef.current, rawCurrentBeat, true);
          // Metronome: click once per whole beat, accenting each bar's downbeat.
          // The toggle and its Ctrl+M binding already existed but nothing ever read
          // the flag, so the feature was inert.
          if (state.metronomeEnabled && rawCurrentBeat > prevBeatRef.current) {
            const prevWhole = Math.floor(prevBeatRef.current);
            const nowWhole = Math.floor(rawCurrentBeat);
            if (nowWhole > prevWhole && rawCurrentBeat >= 0) {
              playTick(nowWhole % 4 === 0 ? 'high' : 'low');
            }
          }
          prevBeatRef.current = rawCurrentBeat;
        }
        // Imperative time/beat readouts (hidden transport — never re-renders).
        const pb = useEditorStore.getState().playback;
        const t  = audio.paused ? pb.currentTime : audio.currentTime;
        if (timeDisplayRef.current) timeDisplayRef.current.textContent = `${formatTime(t)} / ${formatTime(pb.duration)}`;
        if (beatDisplayRef.current) beatDisplayRef.current.textContent = pb.currentBeat.toFixed(1);
        // Playhead — clamped inside the waveform region.
        if (wrapper && playhead) {
          const cssW  = wrapper.clientWidth;
          const dur   = Math.max(pb.duration, 0.01);
          const padX  = TOTAL_HEIGHT / 2;
          const drawW = Math.max(1, cssW - padX * 2);
          const x = Math.max(padX, Math.min(cssW - padX, padX + (t / dur) * drawW));
          playhead.style.left = `${x}px`;
        }
        // Marching-ants overlay for the selected-note time range (matches Flow).
        drawSelectionOverlay(selCanvasRef.current, wrapper, dashOffsetRef);
      }
      animFrameRef.current = requestAnimationFrame(loop);
    };
    animFrameRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [setCurrentTime]);

  // ── Scrub: seek live while dragging (mapped within the inset content region) ──
  // Seeking used to be click-only, so the playhead jumped just once on mouse-up
  // and you could not follow the audio while dragging — the single most-reported
  // issue from mappers. Now a pointerdown seeks immediately and window-level
  // move/up listeners keep seeking for the whole drag (listeners go on `window`
  // so the drag survives the cursor leaving the strip).
  const seekToClientX = useCallback((clientX: number) => {
    const wrapper = wrapperRef.current;
    const audio = audioRef.current;
    if (!wrapper || !audio) return;
    const rect  = wrapper.getBoundingClientRect();
    const padX  = TOTAL_HEIGHT / 2;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left - padX) / Math.max(1, rect.width - padX * 2)));
    const newTime = ratio * Math.max(useEditorStore.getState().playback.duration, 0.01);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
    // Scrubbing backward over already-ticked notes must re-arm them, or their hit
    // sound stays muted on the way back through.
    resetTickState();
    const _s = useEditorStore.getState();
    prevBeatRef.current = ((newTime - (_s.beatmap?.offset || 0) / 1000) / 60) * (_s.beatmap?.bpm || 120);
  }, [setCurrentTime]);

  const handleScrubDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;          // left button only — right-drag is the loop tool
    seekToClientX(e.clientX);
    const onMove = (ev: PointerEvent) => seekToClientX(ev.clientX);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [seekToClientX]);

  // ── Format time ───────────────────────────────────────────────

  return (
    <>
      {/* Waveform pill — matches the Flow / Console strip 1:1. */}
      <div ref={wrapperRef} onPointerDown={handleScrubDown} style={wrapperStyle}>
        <canvas ref={canvasRef} style={canvasStyle} />
        <canvas ref={selCanvasRef} style={selCanvasStyle} />
        <div ref={playheadRef} style={playheadStyle} />
      </div>

      {/* Hidden transport — kept only so the time/beat readout + lock/undo refs survive. */}
      <div style={transportStyle}>
        {/* Left group: playback controls + time */}
        <button
          onClick={() => setPlaying(!pbPlaying)}
          style={{
            ...transportBtnStyle,
            background: pbPlaying
              ? 'linear-gradient(135deg, oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.2), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))'
              : 'rgba(15, 15, 30, 0.4)',
            borderColor: pbPlaying ? 'oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.4)' : 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
            boxShadow: pbPlaying ? '0 0 12px oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.15)' : 'none',
          }}
          title="Space"
        >
          {pbPlaying ? '\u23F8' : '\u25B6'}
        </button>
        <button
          onClick={() => { if (audioRef.current) { audioRef.current.currentTime = 0; setCurrentTime(0); resetTickState(); prevBeatRef.current = 0; } }}
          style={transportBtnStyle}
          title="Rewind to start"
        >
          {'\u23EE'}
        </button>

        <span ref={timeDisplayRef} style={timeDisplayStyle}>
          {formatTime(useEditorStore.getState().playback.currentTime)} / {formatTime(pbDuration)}
        </span>

        <div style={beatInfoStyle}>
          <span>Beat: <span ref={beatDisplayRef} style={{ color: 'oklch(0.82 calc(0.12 * var(--accent-c)) var(--accent-h))' }}>{useEditorStore.getState().playback.currentBeat.toFixed(1)}</span></span>
          {playbackSpeed !== 1 && (
            <span>Speed: <span style={{ color: '#34D399' }}>{playbackSpeed}x</span></span>
          )}
        </div>

        {/* Center group: Undo / Redo */}
        <div style={centerGroupStyle}>
          <button
            onClick={undo}
            disabled={undoStack.length === 0}
            style={transportBtnStyle}
            title="Undo (Ctrl+Z)"
          >
            &#x21B6;
          </button>
          <button
            onClick={redo}
            disabled={redoStack.length === 0}
            style={transportBtnStyle}
            title="Redo (Ctrl+Shift+Z)"
          >
            &#x21B7;
          </button>
        </div>

        {/* Right group: Lock + Song name */}
        <div style={rightGroupStyle}>
          <button
            onClick={() => setMapLocked(!mapLocked)}
            style={{
              ...transportBtnStyle,
              background: mapLocked
                ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(251, 146, 60, 0.12))'
                : 'rgba(15, 15, 30, 0.4)',
              borderColor: mapLocked ? 'rgba(251, 191, 36, 0.4)' : 'rgba(52, 211, 153, 0.2)',
              color: mapLocked ? '#FBBF24' : '#34D399',
              fontSize: 12,
            }}
            title={mapLocked ? 'Map is LOCKED. Click to unlock.' : 'Map is UNLOCKED. Click to lock.'}
          >
            {mapLocked ? '\uD83D\uDD12' : '\uD83D\uDD13'}
          </button>

          <span style={songNameStyle}>
            {beatmap ? `${beatmap.name} \u2014 ${beatmap.bpm} BPM` : 'No beatmap loaded'}
          </span>
        </div>
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} preload="auto" />
    </>
  );
}

// ── Styles (match FlowTimeline) ─────────────────────────────────────────────────

const wrapperStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: TOTAL_HEIGHT,
  borderRadius: TOTAL_HEIGHT / 2,   // fully round (pill) ends
  border: '1px solid rgba(120, 80, 220, 0.18)',
  overflow: 'hidden',
  cursor: 'pointer',
  background: 'oklch(0.075 calc(0.016 * var(--tint-c)) var(--tint-h))',
  flexShrink: 0,
  userSelect: 'none',
};

const canvasStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: TOTAL_HEIGHT,
};

const selCanvasStyle: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  height: TOTAL_HEIGHT,
  pointerEvents: 'none',
};

// Marching-ants overlay for the selected-note time range — white 25% fill +
// animated dashed border. Drawn on its own canvas each RAF frame (so the dash
// animates) without repainting the waveform. Mirrors the Flow timeline.
function drawSelectionOverlay(
  canvas: HTMLCanvasElement | null,
  wrapper: HTMLDivElement | null,
  dashOffsetRef: React.MutableRefObject<number>,
) {
  if (!canvas || !wrapper) return;
  const dpr = window.devicePixelRatio || 1;
  const cssW = wrapper.clientWidth;
  if (cssW <= 0) return;
  if (canvas.width !== Math.floor(cssW * dpr) || canvas.height !== Math.floor(TOTAL_HEIGHT * dpr)) {
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(TOTAL_HEIGHT * dpr);
    canvas.style.width = cssW + 'px';
    canvas.style.height = TOTAL_HEIGHT + 'px';
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, TOTAL_HEIGHT);

  const st = useEditorStore.getState();
  const sel = st.selectedNoteIds;
  const diff = st.beatmap?.difficulties[st.activeDifficulty];
  if (sel.length === 0 || !diff) return;

  let minT = Infinity, maxT = -Infinity;
  for (const i of sel) {
    const n = diff.notes[i];
    if (!n) continue;
    const t = n.time_ms / 1000;
    if (t < minT) minT = t;
    if (t > maxT) maxT = t;
  }
  if (minT === Infinity) return;

  const songDur = Math.max(st.playback.duration, 0.01);
  const PAD_X = TOTAL_HEIGHT / 2;
  const drawW = Math.max(1, cssW - PAD_X * 2);
  const timeToX = (tt: number) => PAD_X + (tt / songDur) * drawW;
  const x1 = timeToX(minT);
  const x2 = Math.max(x1 + 4, timeToX(maxT));

  ctx.fillStyle = 'rgba(255, 255, 255, 0.22)';
  ctx.fillRect(x1, TOP, x2 - x1, CONTENT_H);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([5, 4]);
  ctx.lineDashOffset = -dashOffsetRef.current;
  const inset = 0.75;
  ctx.strokeRect(x1 + inset, TOP + inset, Math.max(1, x2 - x1 - inset * 2), CONTENT_H - inset * 2);

  // Advance the march; wrap by one dash period (5 + 4) for a seamless loop.
  dashOffsetRef.current = (dashOffsetRef.current + 0.4) % 9;
}

const playheadStyle: React.CSSProperties = {
  position: 'absolute',
  top: PAD_Y,
  height: CONTENT_H,
  width: 2,
  marginLeft: -1,
  background: 'var(--accent)',
  boxShadow: '0 0 6px oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.6)',
  pointerEvents: 'none',
  willChange: 'left',
};

const transportStyle: React.CSSProperties = {
  // Hidden — transport (play / time / beat) now lives in the shared playback
  // pill bar above, so the timeline matches Flow (waveform only).
  display: 'none',
};

const transportBtnStyle: React.CSSProperties = {
  background: 'rgba(15, 15, 30, 0.4)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  color: color.ink,
  borderRadius: '50%',
  width: 34,
  height: 34,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  flexShrink: 0,
  cursor: 'pointer',
  fontSize: 14,
  fontFamily: font.sans,
  transition: 'all 0.2s ease',
};

const timeDisplayStyle: React.CSSProperties = {
  color: color.ink,
  fontFamily: font.sans,
  fontSize: 12,
  minWidth: 110,
  fontWeight: 500,
};

const beatInfoStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  color: color.inkFaint,
  fontFamily: font.sans,
  fontSize: 11,
};

const centerGroupStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginLeft: 'auto',
};

const rightGroupStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginLeft: 'auto',
};

const songNameStyle: React.CSSProperties = {
  color: color.inkDim,
  fontFamily: font.sans,
  fontSize: 11,
  fontWeight: 400,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 250,
};
